import { Box, Button, Typography, useTheme } from "@mui/material";
import { SystemUpdateAltRounded } from "@mui/icons-material";
import { openExternal } from "../../lib/platform";

/** Where "Update Insight" sends the user. Resolves to the app's store page
 *  once it is live; before then it lands on the App Store search, which is
 *  harmless because the gate cannot be armed before launch anyway. */
const STORE_URL = "https://apps.apple.com/gb/app/insight-analytics/";

/**
 * The terminal state of the update gate: a full-screen, non-dismissible wall.
 *
 * Non-dismissible is the point. This only ever renders when the API has
 * explicitly declared this binary too old to talk to it safely, so letting the
 * user past it would hand them an app that fails in unpredictable places
 * instead of one place with an explanation.
 *
 * Deliberately plain: no logo lockup, no marketing, no support links to get
 * lost in. The user has exactly one useful action and the screen should be
 * mostly that action.
 */
export const UpdateRequiredScreen = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: theme.zIndex.modal + 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 4,
        pt: "env(safe-area-inset-top, 0px)",
        pb: "env(safe-area-inset-bottom, 0px)",
        backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
      }}
    >
      <SystemUpdateAltRounded
        sx={{ fontSize: 40, color: "#22c55e", mb: 2.5 }}
      />
      <Typography
        component="h1"
        sx={{
          fontSize: "1.35rem",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: theme.palette.text.primary,
          mb: 1.5,
        }}
      >
        Time to update
      </Typography>
      <Typography
        sx={{
          fontSize: "0.9rem",
          lineHeight: 1.6,
          color: theme.palette.text.secondary,
          maxWidth: 320,
          mb: 4,
        }}
      >
        This version of Insight is no longer supported. Update to the latest
        version to carry on where you left off.
      </Typography>
      <Button
        onClick={() => void openExternal(STORE_URL)}
        variant="contained"
        sx={{
          backgroundColor: "#22c55e",
          color: "#ffffff",
          fontWeight: 600,
          textTransform: "none",
          px: 4,
          py: 1.25,
          borderRadius: 2,
          boxShadow: "none",
          "&:hover": { backgroundColor: "#16a34a", boxShadow: "none" },
        }}
      >
        Update Insight
      </Button>
    </Box>
  );
};
